import { type ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import * as React from 'react';
import { Portal } from 'react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';

import { CloseIcon } from '../../icons/Icons';
import { DEFAULT_TRANSITION } from '../../utils';
import { Button } from '../design-system';

import { logTracking } from './LinkWithQuery';

const Video = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => rgba(theme.colors.BLACK, 0.8)};
  z-index: 99999999999;
  padding: 20px;

  video,
  iframe {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    outline: none;
    z-index: 1;
    position: relative;
  }

  .bkg {
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const CloseButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    position: absolute;
    z-index: 2;
    top: 10px;
    right: 10px;
    background-color: ${({ theme }) => theme.colors.BLACK};
    border-left: 1px solid ${({ theme }) => theme.highlightColor};
    border-bottom: 1px solid ${({ theme }) => theme.highlightColor};
    cursor: pointer;
  }
`;

interface FullScreenVideoProps {
  embed?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  path: string;
  trackingLabel: string;
}

export const FullScreenVideo: React.ComponentType<FullScreenVideoProps> =
  React.memo(({ isOpen, onClose, trackingLabel, path, embed }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
      if (isOpen) {
        setTimeout(() => {
          videoRef.current?.play();
        }, 200);

        logTracking({
          label: trackingLabel,
          source: path
        });
      }
    }, [isOpen, path, trackingLabel]);

    const _handleCloseVideo = useCallback(() => {
      videoRef.current?.pause();
      onClose();
    }, [onClose]);

    const ANIMATIONS = {
      FullScreenVideo: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { ...DEFAULT_TRANSITION }
      }
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <Video {...ANIMATIONS.FullScreenVideo}>
              <CloseButton
                icon={<CloseIcon />}
                size="small"
                stylez="bold"
                onClick={_handleCloseVideo}
              />
              {embed || (
                <video ref={videoRef} controls={true} playsInline={true}>
                  <source src={path} type="video/mp4" />
                </video>
              )}
              <div className="bkg" onClick={_handleCloseVideo} />
            </Video>
          </Portal>
        )}
      </AnimatePresence>
    );
  });
